import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { AuthLayout } from './Login';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { useI18n } from '../lib/i18n';

export function Register() {
  const nav = useNavigate();
  const { setAuth } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({ email: '', password: '', displayName: '', city: '' });
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleGoogle(idToken: string) {
    setLoading(true); setErr(null);
    try {
      const r = await api.google(idToken);
      setAuth(r.token, r.user);
      nav('/app');
    } catch (e: any) {
      setErr(e.message ?? 'error');
    } finally { setLoading(false); }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true); setErr(null);
    try {
      const r = await api.register(form);
      setAuth(r.token, r.user);
      nav('/app');
    } catch (e: any) {
      setErr(e.message ?? 'error');
    } finally { setLoading(false); }
  }
  const f = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <AuthLayout>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-sm rounded-3xl p-7 text-center"
      >
        <h1 className="h-display text-3xl">{t('auth.register.title')}</h1>
        <p className="mt-1 text-haze text-sm">{t('auth.register.subtitle')}</p>

        <div className="mt-6">
          <GoogleSignInButton onCredential={handleGoogle} />
        </div>

        <div className="my-6 flex items-center gap-3 text-2xs uppercase tracking-widest text-haze">
          <span className="flex-1 divider" />
          <span>{t('auth.login.orEmail')}</span>
          <span className="flex-1 divider" />
        </div>

        <form onSubmit={submit} className="text-left space-y-3">
          <input value={form.displayName} onChange={f('displayName')} placeholder={t('auth.register.namePlaceholder')} required className="input" />
          <input value={form.city} onChange={f('city')} placeholder={t('auth.register.cityPlaceholder')} className="input" />
          <input type="email" value={form.email} onChange={f('email')} placeholder={t('auth.login.emailPlaceholder')} required className="input" />
          <input type="password" minLength={8} value={form.password} onChange={f('password')} placeholder={t('auth.login.passwordPlaceholder') + ' (8+)'} required className="input" />
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? t('auth.register.submitting') : t('auth.register.submit')}
          </button>
        </form>

        {err && <p className="mt-4 text-sm text-flame">{err}</p>}

        <p className="mt-5 text-sm text-haze">
          {t('auth.register.hasAccount')} <Link to="/login" className="text-foam font-semibold underline">{t('auth.register.signIn')}</Link>
        </p>
      </motion.div>
    </AuthLayout>
  );
}
